import React, { useEffect, useRef, useState } from "react";
import { C, font, mono, inp, lbl, sel, btnP, btnS } from "../../../lib/styles.js";
import { computeDroneLineItems, droneEstimatorDefaults, normalizeDroneBlock, isDroneCatalogId, cleanGroupLabel, cleanGroupNote, fmtAcres } from "../../../lib/builderHelpers.js";
import { DRONE_MODEL } from "../../../lib/constants.js";
import { fmtMoney } from "../../../lib/utils.js";

/*
  DroneEstimator — Quote Builder widget for aerial photogrammetry (DJI Mavic 3E), the
  sibling of LidarEstimator. Captures the operator's inputs, live-previews the derived
  price via the shared computeDroneLineItems() calculator (single source of truth =
  DRONE_MODEL, approved by Ivin 2026-09-04), and on Insert appends the computed line
  items to the quote: Capture / Processing / Rush / Overtime, each its own line, each
  carrying a structured `drone` audit block.

  Modes: capture + processing, capture only (site flown, stills archived, nothing
  delivered), processing only (stills already captured). Days multiply the per-day lines
  (capture, overtime); acres are a job total.

  Blocks: v1 has no AI prefill. `initial` may carry ONE block ({ label, note }) handed in
  by the builder so the lines land under a named group; the block list plumbing is
  deliberately zero-or-one. Setups became acres and photogrammetry became the
  deliverable switch; everything else mirrors the LiDAR widget.

  Target (2026-09-04): `target` ({ label, note }) is the builder's "Adding to" block. It
  seeds the Block and Heading note fields at mount and re-seeds just those two fields
  when the target changes while the widget is open (crew, acres and the rest keep what
  was typed), exactly as LidarEstimator does. Both fields stay editable: a different
  name creates or joins a different block. Drone has no brief walk, so there is no
  pending rule: the target applies whenever the builder passes one.
*/

const MODES = [["processing", "Capture + processing"], ["captureOnly", "Capture only"], ["processingOnly", "Processing only"]];
const DAY_TYPES = [["standard", `Standard (${DRONE_MODEL.capture.dayHours.standard} hr)`], ["film", `Film & TV (${DRONE_MODEL.capture.dayHours.film} hr)`]];
const DELIVERABLES = [["geometry", "Geometry only (matchmove)"], ["textured", "Textured mesh"]];

const isWhole = (v, min, max) => {
  if (v === null || v === undefined || String(v).trim() === "") return false;
  const n = Number(v);
  return Number.isInteger(n) && n >= min && n <= max;
};
const isPositive = (v, max) => {
  if (v === null || v === undefined || String(v).trim() === "") return false;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 && n <= max;
};
const isBlank = (v) => v === null || v === undefined || String(v).trim() === "";
const isNumberIn = (v, min, max) => {
  if (isBlank(v)) return false;
  const n = Number(v);
  return Number.isFinite(n) && n >= min && n <= max;
};

export default function DroneEstimator({ onInsert, onClose, initial = null, industry = "other", lineItems = [], target = null }) {
  const M = DRONE_MODEL;
  const block = normalizeDroneBlock(initial);
  const [form, setForm] = useState(() => ({
    ...droneEstimatorDefaults(block, industry),
    ...(target ? { label: cleanGroupLabel(target.label), note: cleanGroupNote(target.note) } : {}),
  }));
  const upd = (patch) => setForm((f) => ({ ...f, ...patch }));
  // "Adding to" changed while open: only the Block and Heading note follow it.
  const targetLabel = target ? cleanGroupLabel(target.label) : null;
  const targetNote = target ? cleanGroupNote(target.note) : null;
  // Switching the pill to "No block" while open clears the two fields again, so the next
  // Insert cannot land in a block the operator just left (GO review, 2026-09-04).
  const prevTarget = useRef(targetLabel);
  useEffect(() => {
    const had = prevTarget.current != null;
    prevTarget.current = targetLabel;
    if (targetLabel != null) setForm((f) => ({ ...f, label: targetLabel, note: targetNote }));
    else if (had) setForm((f) => ({ ...f, label: "", note: "" }));
  }, [targetLabel, targetNote]);

  const isProcessingOnly = form.mode === "processingOnly";
  const hasCapture = !isProcessingOnly;
  const hasProcessing = form.mode !== "captureOnly";
  const isPartial = form.timeBand === "partial";  // overtime cannot happen on a 0 to 3 hr day
  const dayHours = M.capture.dayHours[form.dayType === "film" ? "film" : "standard"];

  const label = cleanGroupLabel(form.label);
  const note = cleanGroupNote(form.note);
  const result = computeDroneLineItems({ ...form, label });
  const items = result.items || [];
  // Whole-number guards on crew and days, and a positive figure for acres whenever
  // processing is priced: the calculator would otherwise round or clamp and price
  // something the box does not show. Processing only has no crew or days to validate.
  // Capture only records the acres without pricing them: a blank box records nothing,
  // a figure must be a number of 0 or more (the calculator would clamp -3 to 0).
  const crewOk = !hasCapture || isWhole(form.crewSize, 1, 12);
  const daysOk = !hasCapture || isWhole(form.days, 1, 30);
  const acresOk = hasProcessing ? isPositive(form.acres, 100000) : (isBlank(form.acres) || isNumberIn(form.acres, 0, 100000));
  const acresMsg = hasProcessing ? "Enter the acres flown as a number above 0." : "Enter the acres flown as a number of 0 or more.";
  // Overtime and the turnaround are guarded too (2026-09-04): the calculator would clamp
  // a typed -5 to 0 or fall back to the default and price something the box does not
  // show. A blank overtime box means no overtime; a blank turnaround is not a number.
  const overtimeOk = !hasCapture || isPartial || isBlank(form.overtimeHrs) || isNumberIn(form.overtimeHrs, 0, 24);
  const processingDaysOk = !hasProcessing || !!form.rush || isWhole(form.processingDays, 1, 365);
  const err = result.error
    || (!crewOk ? "Enter the crew on site as a whole number from 1 to 12." : undefined)
    || (!daysOk ? "Enter the capture days as a whole number from 1 to 30." : undefined)
    || (!acresOk ? acresMsg : undefined)
    || (!overtimeOk ? "Enter overtime as a number from 0 to 24." : undefined)
    || (!processingDaysOk ? "Enter the processing turnaround as a whole number of business days from 1 to 365." : undefined);
  const total = items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.rate) || 0), 0);
  const includedUpTo = items.find((it) => it.drone?.kind === "processing")?.drone?.includedUpTo;
  const turnaroundDays = form.rush ? M.rush.turnaroundDays : Math.max(1, Math.round(Number(form.processingDays) || M.processing.defaultDays));
  const days = Math.max(1, Math.round(Number(form.days) || 1));

  // A labeled block that already has drone lines on the quote is being inserted again.
  const alreadyIn = !!label && (lineItems || []).some((li) => isDroneCatalogId(li?.catalogId) && cleanGroupLabel(li?.drone?.block) === label);

  const insert = () => {
    if (err || !items.length) return;
    onInsert(items, { label, note });
    onClose();
  };

  const primaryText = `Insert line item${items.length > 1 ? "s" : ""}${alreadyIn ? " again" : ""}`;

  return (
    <div style={ST.wrap} data-testid="drone-estimator">
      <div style={ST.grid}>
        <div style={{ ...ST.field, gridColumn: "1 / -1" }}>
          <label style={lbl}>Block</label>
          <input type="text" maxLength={60} style={ST.text} value={form.label || ""} placeholder="Names this scope on every line and as its heading, for example Pile. Leave empty for a single scope."
            onChange={(e) => upd({ label: e.target.value })} />
          {target && label && <span style={ST.cap}>Lines will land in the {label} block.</span>}
        </div>
        {(form.label || "").trim() !== "" && (
          <div style={{ ...ST.field, gridColumn: "1 / -1" }}>
            <label style={lbl}>Heading note</label>
            <input type="text" maxLength={120} style={ST.text} value={form.note || ""} placeholder="Optional line under the heading, for example the date or the place."
              onChange={(e) => upd({ note: e.target.value })} />
          </div>
        )}

        <div style={ST.field}>
          <label style={lbl}>Mode</label>
          <div style={ST.radios}>
            {MODES.map(([v, text]) => (
              <button key={v} type="button" onClick={() => upd({ mode: v })}
                style={{ ...ST.pill, ...(form.mode === v ? ST.pillOn : {}) }}>{text}</button>
            ))}
          </div>
        </div>

        {hasCapture && (
          <div style={ST.field}>
            <label style={lbl}>Day type</label>
            <div style={ST.radios}>
              {DAY_TYPES.map(([v, text]) => (
                <button key={v} type="button" onClick={() => upd({ dayType: v })}
                  style={{ ...ST.pill, ...(form.dayType === v ? ST.pillOn : {}) }}>{text}</button>
              ))}
            </div>
          </div>
        )}

        {hasCapture && (
          <div style={ST.field}>
            <label style={lbl}>Time on site</label>
            <select style={ST.selField} value={form.timeBand} onChange={(e) => upd({ timeBand: e.target.value, ...(e.target.value === "partial" ? { overtimeHrs: 0 } : {}) })}>
              <option value="partial">Partial day (0 to {M.capture.partialHours} hrs)</option>
              <option value="full">Full day ({dayHours} hrs)</option>
            </select>
          </div>
        )}

        {hasCapture && (
          <div style={ST.field}>
            <label style={lbl}>Crew on site (pilot + observer)</label>
            <input type="number" min="1" max="12" step="1" style={ST.num} value={form.crewSize}
              onChange={(e) => upd({ crewSize: e.target.value })} />
          </div>
        )}

        <div style={ST.field}>
          <label style={lbl}>{hasProcessing ? "Acres flown" : "Acres flown (recorded, not priced)"}</label>
          <input type="number" min="0" step="0.1" style={ST.num} value={form.acres}
            onChange={(e) => upd({ acres: e.target.value })} />
        </div>

        {hasProcessing && (
          <div style={ST.field}>
            <label style={lbl}>Deliverable</label>
            <div style={ST.radios}>
              {DELIVERABLES.map(([v, text]) => (
                <button key={v} type="button" onClick={() => upd({ deliverable: v })}
                  style={{ ...ST.pill, ...(form.deliverable === v ? ST.pillOn : {}) }}>{text}</button>
              ))}
            </div>
          </div>
        )}

        {hasProcessing && (
          <div style={ST.field}>
            <label style={lbl}>{`Processing turnaround (biz days)${form.rush ? ", set by rush" : ""}`}</label>
            <input type="number" min="1" disabled={form.rush}
              style={{ ...ST.num, ...(form.rush ? ST.numDisabled : {}) }}
              value={form.rush ? M.rush.turnaroundDays : form.processingDays}
              onChange={(e) => upd({ processingDays: e.target.value })} />
          </div>
        )}

        {hasCapture && (
          <div style={ST.field}>
            <label style={lbl}>{`Overtime hrs (past ${dayHours})${isPartial ? ", full day only" : ""}${days > 1 ? ", per day" : ""}`}</label>
            <input type="number" min="0" step="0.5" disabled={isPartial}
              style={{ ...ST.num, ...(isPartial ? ST.numDisabled : {}) }}
              value={isPartial ? 0 : form.overtimeHrs}
              onChange={(e) => upd({ overtimeHrs: e.target.value })} />
          </div>
        )}

        {hasCapture && (
          <div style={ST.field}>
            <label style={lbl}>Capture days</label>
            <input type="number" min="1" max="30" step="1" style={ST.num} value={form.days}
              onChange={(e) => upd({ days: e.target.value })} />
          </div>
        )}
      </div>

      {hasProcessing && (
        <div style={ST.checks}>
          <label style={ST.check}>
            <input type="checkbox" checked={form.rush} onChange={(e) => upd({ rush: e.target.checked })} />
            Rush processing (starts right after capture, {M.rush.turnaroundDays} business day turnaround; {Math.round(M.rush.processingMultiplier * 100)}% of processing{M.rush.minimum > 0 ? `, ${fmtMoney(M.rush.minimum)} minimum` : ""})
          </label>
        </div>
      )}

      {err ? (
        <div style={ST.err}>{err}</div>
      ) : (
        <div style={ST.preview}>
          {items.map((it, i) => (
            <div key={i} style={ST.liRow}>
              <div>
                <div style={ST.liDesc}>{it.description}{it.qty > 1 ? <span style={ST.liQty}> × {it.qty}</span> : null}</div>
                <div style={ST.liNotes}>{it.notes}</div>
              </div>
              <div style={ST.liRate}>{fmtMoney(it.rate * (it.qty || 1))}</div>
            </div>
          ))}
          <div style={ST.total}><span>Total</span><span style={{ fontFamily: mono }}>{fmtMoney(total)}</span></div>
          {hasProcessing && (
            <div style={ST.procNote}>
              Processing turnaround: ~{turnaroundDays} business day{turnaroundDays === 1 ? "" : "s"} from {isProcessingOnly ? "receipt of the stills" : "capture"}
              {includedUpTo ? ` · up to ${fmtAcres(includedUpTo)} acres included before a change order` : ""}
            </div>
          )}
        </div>
      )}

      <div style={ST.foot}>
        <button type="button" style={btnS} onClick={onClose}>Cancel</button>
        <button type="button" style={{ ...btnP, opacity: err || !items.length ? 0.4 : 1, cursor: err ? "not-allowed" : "pointer" }}
          disabled={!!err || !items.length} onClick={insert}>{primaryText}</button>
      </div>
    </div>
  );
}

const ST = {
  wrap: { background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: 14, marginBottom: 12, display: "grid", gap: 12 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12 },
  field: { display: "grid", gap: 4 },
  radios: { display: "flex", gap: 6, flexWrap: "wrap" },
  pill: { ...btnS, padding: "8px 12px", fontSize: 12 },
  pillOn: { background: C.text, color: "#FFF", borderColor: C.text },
  num: { ...inp, background: C.surface, fontWeight: 400 },
  text: { ...inp, background: C.surface, fontWeight: 400 },
  cap: { fontSize: 12, color: C.muted, marginTop: 2 },
  numDisabled: { opacity: 0.45, cursor: "not-allowed" },
  selField: { ...sel, backgroundColor: C.surface },
  checks: { display: "grid", gap: 8 },
  check: { display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: C.text, cursor: "pointer", fontFamily: font },
  preview: { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6, padding: "12px 14px", display: "grid", gap: 8 },
  liRow: { display: "flex", justifyContent: "space-between", gap: 12, alignItems: "baseline" },
  liDesc: { color: C.text, fontSize: 12.5, fontWeight: 400 },
  liQty: { color: C.muted, fontFamily: mono, fontSize: 12 },
  liNotes: { color: C.muted, fontSize: 11.5, marginTop: 2 },
  liRate: { fontFamily: mono, color: C.text, fontSize: 13, whiteSpace: "nowrap" },
  total: { display: "flex", justifyContent: "space-between", borderTop: `1px solid ${C.border}`, paddingTop: 8, fontWeight: 500, fontSize: 13.5, color: C.text },
  procNote: { fontSize: 12, color: C.muted, marginTop: 4 },
  err: { color: C.danger, fontSize: 12.5, background: `${C.danger}10`, border: `1px solid ${C.danger}33`, borderRadius: 6, padding: "10px 12px" },
  foot: { display: "flex", justifyContent: "flex-end", gap: 8 },
};
